<template>
  <v-container
    fluid
    class="list-page"
    :class="{ 'list-page--embedded invoice-list-embedded': isEmbedded }"
  >
    <div v-if="!isEmbedded" class="list-page-heading">
      <div>
        <div class="list-page-heading__eyebrow">Billing</div>
        <h1>Invoices</h1>
        <p>Review charges, balances, payments, and collection activity.</p>
      </div>
      <div class="list-page-heading__actions">
        <v-btn
          v-if="checkRightStatus(57)"
          color="primary"
          depressed
          @click="add"
        >
          <v-icon left>mdi-plus</v-icon>
          New invoice
        </v-btn>
      </div>
    </div>
    <v-row class="list-metrics invoice-metrics">
        <v-col cols="6" sm="4" md="3">
          <div class="list-metric">
            <div class="list-metric__icon">
              <v-icon color="primary" size="21">mdi-receipt-text-outline</v-icon>
            </div>
            <div>
              <div class="list-metric__value">{{ Invoices.length }}</div>
              <div class="list-metric__label">
                {{ isEmbedded ? "Invoices" : "Invoices in result" }}
              </div>
            </div>
          </div>
        </v-col>

        <v-col cols="6" sm="4" md="3">
          <div class="list-metric">
            <div class="list-metric__icon">
              <v-icon color="primary" size="21">mdi-cash-clock</v-icon>
            </div>
            <div>
              <div class="list-metric__value">{{ fullBalance | currency }}</div>
              <div class="list-metric__label">Outstanding balance</div>
            </div>
          </div>
        </v-col>

        <v-col cols="6" sm="4" md="3">
          <div class="list-metric">
            <div class="list-metric__icon">
              <v-icon color="primary" size="21">mdi-cash-check</v-icon>
            </div>
            <div>
              <div class="list-metric__value">{{ fullPayment | currency }}</div>
              <div class="list-metric__label">Payments received</div>
            </div>
          </div>
        </v-col>

        <v-col
          v-if="isEmbedded && checkRightStatus(57)"
          cols="12"
          sm="4"
          md="3"
          class="invoice-metric-action"
        >
          <v-btn color="primary" depressed @click="add">
            <v-icon left>mdi-plus</v-icon>
            New invoice
          </v-btn>
        </v-col>
      </v-row>

    <v-card :loading="loading" class="list-card" elevation="0">
      <div class="list-toolbar invoice-toolbar">
        <v-text-field
          v-model="search"
          class="list-toolbar__search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search invoices, users, parents, or donors"
          single-line
          hide-details
          clearable
        />
        <div class="list-toolbar__actions invoice-toolbar-actions">
        <v-btn-toggle
          v-if="toggleStatus === 'show'"
          v-model="toggleChange"
          mandatory
          class="audience-filter"
        >
          <v-btn small value="all"
            ><v-icon left small>mdi-format-list-bulleted</v-icon>All</v-btn
          >
          <v-btn small value="parents"
            ><v-icon left small>mdi-account-outline</v-icon>Parents</v-btn
          >
          <v-btn small value="donors"
            ><v-icon left small>mdi-hand-coin-outline</v-icon>Donors</v-btn
          >
        </v-btn-toggle>
        <div class="date-filter list-toolbar__control" v-if="!showAll">
          <DateTimePicker
            defaultRange="today"
            :range="true"
            :hideTime="true"
            v-model="dateTime"
            @submit="getList()"
          />
        </div>

        <v-btn
          icon
          outlined
          color="primary"
          :loading="loading"
          aria-label="Refresh invoices"
          @click="getList"
        >
          <v-icon size="19">mdi-refresh</v-icon>
        </v-btn>
        </div>
      </div>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="Invoices"
          @current-items="updateTotal"
          :search="search"
          :sort-desc="sortDesc"
          :loading="loading"
          :sort-by="sortBy"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          class="list-table invoice-table"
        >
          <template v-slot:item="{ item }">
            <tr class="list-table-row pointer">
              <td @click="viewInvoice(item)">
                <span class="invoice-number">#{{ item.localId }}</span>
                <v-tooltip bottom v-if="item.due !== 0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      small
                      icon
                      class="ma-0 pa-0"
                      @click="setAutoCollect(item)"
                    >
                      <v-icon color="error" v-if="item.collectionStatus === 'exhausted'">mdi-alert-circle</v-icon>
                      <v-icon color="primary" v-else-if="item.autoCollect"
                        >mdi-check-circle</v-icon
                      >
                      <v-icon v-else dark>mdi-check-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span v-if="item.collectionStatus === 'exhausted'">Resume Auto Collect</span>
                  <span v-else-if="item.autoCollect">Disable Auto Collect</span>
                  <span v-else>Enable Auto Collect</span>
                </v-tooltip>
              </td>
              <td @click="viewInvoice(item)" class="text-left">
                <span v-if="item.User">{{ item.User.name }}</span>
              </td>
              <td class="text-left" @click="viewProfileNewTab(item)">
                <span>
                  <v-icon color="primary" size="18">{{
                    item.Customer
                      ? "mdi-account-outline"
                      : "mdi-hand-coin-outline"
                  }}</v-icon>

                  <span class="invoice-account-name">{{ item.name }}</span>
                  <span
                    class="ml-2"
                    v-if="item.due !== 0 && item.nextChargeDate"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-chip small label v-on="on" color="grey lighten-4">
                          {{ item.nextChargeDate | moment("MMM DD") }}
                          <v-icon small right>
                            mdi-clock-time-eight-outline
                          </v-icon>
                        </v-chip>
                      </template>
                      <span>Next Charge Try</span>
                    </v-tooltip>
                  </span>

                  <span class="ml-1">
                    <v-tooltip
                      bottom
                      class="ml-1"
                      v-if="
                        (item.Customer && item.Customer.payCompassId) ||
                        (item.Donor && item.Donor.payCompassId)
                      "
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-avatar
                          size="10"
                          color="yellow"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <span>Set for online payment</span>
                    </v-tooltip>
                  </span>

                  <!--
                  <span v-if="item.Customer" class="ml-1">
                    <v-tooltip bottom v-if="item.Customer.payCompassId">
                      <template v-slot:activator="{ on, attrs }">
                        <v-avatar size="10" color="yellow" v-bind="attrs" v-on="on" />
                      </template>
                      <span>Set for online payment</span>
                    </v-tooltip>
                  </span>
                  <span v-else class="ml-1">
                    <v-tooltip bottom v-if="item.Donor.payCompassId">
                      <template v-slot:activator="{ on, attrs }">
                        <v-avatar size="10" color="yellow" v-bind="attrs" v-on="on" />
                      </template>
                      <span>Set for online payment</span>
                    </v-tooltip>
                  </span> -->
                </span>

                <span class="ml-1">
                  <v-chip v-if="item.due === 0" x-small color="primary" outlined
                    >Paid</v-chip
                  >
                </span>
                <span class="ml-1">
                  <v-chip
                    v-if="item.refundTotal !== 0"
                    pill
                    small
                    color="blue-grey lighten-5"
                    text-color="blue-grey darken-4"
                  >
                    <v-icon left color="blue-grey darken-4" small>
                      mdi-cash-refund
                    </v-icon>
                    {{ item.refundTotal | currency }} Refund</v-chip
                  >
                </span>

              </td>
              <td class="text-left pointer" @click="viewInvoice(item)">
                {{ item.date | moment("MM/DD/YYYY") }}
              </td>
              <td class="text-left pointer">
                <span @click="viewInvoice(item)">{{
                  item.dueDate | moment("MM/DD/YYYY")
                }}</span>

                <v-tooltip bottom v-if="item.transactionCount > 0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      v-bind="attrs"
                      v-on="on"
                      small
                      color="primary"
                      outlined
                      class="ml-2 font-weight-bold collection-attempt-chip"
                      @click.stop="openTransaction(item)"
                      label
                    >
                      <v-icon left small>mdi-refresh</v-icon>
                      {{ collectionAttemptLabel(item.transactionCount) }}
                    </v-chip>
                  </template>
                  <span
                    >View {{ collectionAttemptLabel(item.transactionCount) }} to
                    collect payment</span
                  >
                </v-tooltip>
              </td>
              <!-- <td :class="ConditionalClasses(item)" >
                {{ item.transactionCount }}
              </td> -->
              <!-- <td class="text-right pointer" @click="viewInvoice(item)">
                {{ item.total | currency }}
              </td>
              <td class="text-right pointer" @click="viewInvoice(item)">
                {{ item.payment | currency }}
              </td> -->
              <td class="text-right pointer" @click="viewInvoice(item)">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <span v-if="item.due !== 0">{{
                        item.due | currency
                      }}</span>
                      <span v-else>{{ item.total | currency }}</span>
                    </span>
                  </template>
                  <div>
                    <span>Total: {{ item.total | currency }}</span
                    ><br />
                    <span>Payment: {{ item.payment | currency }}</span>
                  </div>
                </v-tooltip>
              </td>
              <td class="justify-end align-center text-right pointer">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon aria-label="Invoice actions">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list class="menu-button">
                    <v-list-item @click="viewInvoice(item)">
                      <v-list-item-title>View Invoice</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="item.due > 0"
                      @click="receivePayment(item)"
                    >
                      <v-list-item-title>Receive Payment</v-list-item-title>
                    </v-list-item>
                    <!-- <v-list-item @click="viewPackingSlip(item)">
                        <v-list-item-title>View Packing Slip</v-list-item-title>
                      </v-list-item> -->
                    <v-divider v-if="!DonorId && !CustomerId"></v-divider>
                    <v-list-item
                      v-if="!DonorId && !CustomerId"
                      @click="viewProfile(item)"
                    >
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item
                      v-if="item.payment > 0"
                      @click="refundInvoice(item)"
                    >
                      <v-list-item-title>Refund</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="checkRightStatus(59)"
                      @click="editInvoice(item)"
                    >
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.due !== 0 && checkRightStatus(60)"
                      @click="deleteInvoice(item)"
                    >
                      <v-list-item-title>Delete</v-list-item-title>
                    </v-list-item>
                    <ShareLink
                      :id="item.id"
                      :postfix="`view?key=${currentBusiness.code}`"
                      type="invoice"
                      :text="`${currentBusiness.name} Invoice click here to view`"
                    />
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
          <v-alert slot="no-results" :value="true" color="error" icon="warning"
            >Your search for "{{ search }}" found no results.</v-alert
          >
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-dialog v-model="loadingDialog" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-container>
          <div class="mb-3">Please stand by deleting Invoice</div>
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-container>
      </v-card>
    </v-dialog>

    <TransactionDialog
      :openTransactionDialog="openTransactionDialog"
      @close="openTransactionDialog = false"
      :selectedInvoice="selectedInvoice"
    />

    <InvoicePaymentReceive
      @close="openReceivePaymentDialog = false"
      :openReceivePaymentDialog="openReceivePaymentDialog"
      :Invoice="selectedInvoice"
    ></InvoicePaymentReceive>

    <RefundDialog
      :refundDialog="refundDialog"
      @close="refundDialog = false"
      @submit="getList"
      :invoiceId="selectedInvoice ? selectedInvoice.id : null"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import invoiceService from "@/modules/Invoice/service.js";
import ShareLink from "@/components/ShareLink";
const DateTimePicker = () => import("@/components/DateTimePicker");
// import paymentService from "@/modules/Customer/Payment/service.js";

import Hashids from "hashids";
const hashids = new Hashids();

// import stripeService from "@/modules/Stripe/service.js";
import TransactionDialog from "@/modules/Invoice/components/Transaction.vue";
import RefundDialog from "@/modules/Invoice/components/Refund.vue";
const InvoicePaymentReceive = () =>
  import("@/modules/Invoice/Payment/components/InvoicePaymentCreate.vue");

export default {
  props: {
    toggleStatus: {
      default: "show",
    },
    CustomerId: {
      default: null,
    },
    DonorId: {
      default: null,
    },
    showAll: {
      default: false,
    },
    due: {
      default: false,
    },
    internalUse: {
      default: false,
    },
  },
  name: "invoice-list",
  data() {
    return {
      paymentMethods: [
        "Stripe",
        "Mohid",
        "Cash",
        "Check",
        "Paypal",
        "Debit Card",
        "Credit Card",
        "Wire",
        "Quick Pay",
        "Cash App",
        "Exchange",
        "Masjid",
        "Other",
      ],
      paymentType: "manual",
      openTransactionDialog: false,
      showCustomersOnly: false,
      showDonorsOnly: false,
      toggleChange: "all",
      Payment: {
        CustomerId: 0,
        date: this.$moment().toISOString(),
        referenceNumber: null,
        donationReferenceNumber: null,
        paymentMethod: "Stripe",
        donationPaymentMethod: "Masjid",
        PaymentDetails: [],
        amount: 0,
        donationAmount: 0,
        donation: false,
        parent: true,
      },
      openReceivePaymentDialog: false,
      loadingBtn: false,
      refundDialog: false,
      selectedInvoice: null,
      cards: [],
      amount: 0,
      paymentDialog: false,
      loadingDialog: false,
      fullTotal: 0,
      fullPayment: 0,
      fullBalance: 0,
      total: 0,
      balance: 0,
      fab: false,
      dateTime: {
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      createDialog: false,
      selectedPayee: null,
      headers: [],
      Invoices: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "date",
      pageCount: 0,
      sortDesc: true,
      itemsPerPage: 10,
    };
  },
  watch: {
    paymentType(val) {
      console.log("val", val, this.Payment);
      if (val === "online") {
        this.Payment.date = this.$moment().toISOString();
      } else {
        console.log("here in munal", this.selectedInvoice);
        this.Payment.date = this.$moment(
          this.selectedInvoice.date
        ).toISOString();
      }
    },
    paymentDialog(val) {
      if (val) {
        if (
          this.selectedInvoice &&
          this.selectedInvoice.Customer &&
          this.selectedInvoice.Customer.payCompassId
        ) {
          this.getCard(this.selectedInvoice.Customer.payCompassId);
        }
      }
    },
    DonorId: {
      immediate: true,
      handler() {
        this.getList();
      },
    },
    toggleChange(val) {
      if (val === "donors") {
        this.showDonorsOnly = true;
        this.showCustomersOnly = false;
      } else if (val === "parents") {
        this.showCustomersOnly = true;
        this.showDonorsOnly = false;
      } else {
        this.showCustomersOnly = false;
        this.showDonorsOnly = false;
      }
      this.getList();
    },
  },
  created() {
    this.loadHeader();
    this.$events.listen("invoiceListEvent", () => this.getList());
    // document.title = "taleem - Invoice List";
  },
  beforeDestroy() {
    this.$events.remove("invoiceListEvent");
  },
  components: {
    InvoicePaymentReceive,
    DateTimePicker,
    ShareLink,
    TransactionDialog,
    RefundDialog,
  },
  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
    ...mapGetters("customer", ["getCustomer"]),
    ...mapGetters("invoice", ["InvoiceDetails", "getEditInvoiceId"]),
    fullFlexSize() {
      if (this.Payment.donation && this.Payment.parent) return true;
      return false;
    },
    isEmbedded() {
      return Boolean(this.CustomerId || this.DonorId || this.internalUse);
    },
  },
  methods: {
    ...mapActions("invoice", ["ResetInvoice"]),

    add() {
      if (this.DonorId) {
        this.$router.push({
          path: `/invoice/create?DonorId=${this.DonorId}`,
        });
      } else if (this.CustomerId) {
        this.$router.push({
          path: `/invoice/create?CustomerId=${this.CustomerId}`,
        });
      } else {
        this.$router.push({ path: "/invoice/create" });
      }
    },
    reset() {
      this.Payment = {
        CustomerId: 0,
        date: this.$moment().toISOString(),
        referenceNumber: null,
        paymentMethod: "Stripe",
        PaymentDetails: [],
        amount: 0,
        donation: false,
        parent: true,
      };
      this.paymentType = "manual";
    },
    close() {
      this.reset();
      this.paymentDialog = false;
      this.refundDialog = false;
    },
    ConditionalClasses(val) {
      if (val.transactionCount)
        return "text-center text-decoration-underline blue--text pointer";
      return "text-center text-decoration-underline blue--text";
    },
    refund() {
      if (!this.selectedCard && this.paymentType === "online") {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Select card on file",
          });
        return false;
      }
      if (this.Payment.amount > 0) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Refund amount can't be greater than 0",
          });
        return false;
      }
      if (this.Payment.amount * -1 > this.selectedInvoice.payment) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "You can't refund amount more than payment",
          });
        return false;
      }
      this.loadingBtn = true;
      this.Payment.CustomerId = this.selectedInvoice.CustomerId;
      this.Payment.PaymentDetails.push({
        amount: this.Payment.amount,
        referenceNumber: this.Payment.referenceNumber,
        InvoiceId: this.selectedInvoice.id,
        donation: false,
      });
      if (this.paymentType === "online") {
        this.refundPayment();
      } else {
        this.savePayment();
      }
    },
    setAutoCollect(invoice) {
      this.loading = true;
      const autoCollect = invoice.collectionStatus === 'exhausted' ? true : !invoice.autoCollect;
      invoiceService
        .update(invoice.id, { autoCollect })
        .then((response) => {
          if (response.data) {
            invoice.autoCollect = autoCollect;
            invoice.collectionStatus = autoCollect ? 'pending' : 'disabled';
            this.loading = false;
          }
        })
        .catch((error) => {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: error.data.message,
            });
          this.loading = false;
        });
    },
    receivePayment(val) {
      this.selectedInvoice = val;
      this.openReceivePaymentDialog = !this.openReceivePaymentDialog;
    },
    openTransaction(val) {
      this.selectedInvoice = val;
      this.openTransactionDialog = !this.openTransactionDialog;
    },
    collectionAttemptLabel(count) {
      return `${count} ${count === 1 ? "attempt" : "attempts"}`;
    },
    async receivePayment1(val) {
      this.selectedInvoice = (await invoiceService.getById(val.id)).data;
      console.log("payment receive", val, this.selectedInvoice);
      let dueAmount = 0;
      // this.selectedInvoiceParentPayDue = dueAmount
      this.selectedInvoice.InvoiceDetails.map((row) => {
        dueAmount += row.parentPay;
      });
      if (dueAmount === 0) {
        this.Payment.amount = val.due;
        this.Payment.donationAmount = this.selectedInvoice.due - val.due;
        this.selectedInvoiceParentPayDue = val.due;
      } else {
        this.Payment.amount = dueAmount;
        this.Payment.donationAmount = this.selectedInvoice.due - dueAmount;
        this.selectedInvoiceParentPayDue = dueAmount;
      }
      this.Payment.date = this.$moment(val.date).toISOString();
      this.paymentDialog = !this.paymentDialog;
    },
    async refundInvoice(val) {
      this.selectedInvoice = val;
      this.refundDialog = !this.refundDialog;
    },
    updateTotal(val) {
      this.total = 0;
      this.payment = 0;
      this.balance = 0;
      val.map((row) => {
        this.total += row.total;
        this.payment += row.payment;
        this.balance += row.due;
      });
    },
    viewProfileNewTab(val) {
      if (this.internalUse) {
        return false;
      }
      let routeData = null;
      if (val.DonorId) {
        routeData = this.$router.resolve({
          path: `/donors/${val.DonorId}/profile`,
        });
      } else {
        routeData = this.$router.resolve({
          path: `/parents/${val.CustomerId}/profile`,
        });
      }
      window.open(routeData.href, "_blank");
    },
    viewProfile(val) {
      if (val.DonorId) {
        this.$router.push({ path: `/donors/${val.DonorId}/profile` });
      } else {
        this.$router.push({ path: `/parents/${val.CustomerId}/profile` });
      }
    },
    viewInvoice(invoice) {
      if (invoice.DonorId) {
        this.donorInvoice(invoice);
      } else {
        this.openInvoice(invoice);
      }
    },
    openInvoice(invoice) {
      console.log("invoice", invoice.id);
      const hashId = hashids.encode(invoice.id);
      console.log("dueAmount", `localhost:8082/global/invoice/${hashId}/view`);
      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/view`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    donorInvoice(invoice) {
      console.log("invoice", invoice.id);
      const hashId = hashids.encode(invoice.id);
      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/donorInvoice`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    loadHeader() {
      this.headers = [];
      this.headers.push({
        text: "#",
        align: "left",
        value: "localId",
        sortable: true,
      });
      this.headers.push({
        text: "User Name",
        align: "left",
        value: "User.name",
        sortable: true,
      });
      this.headers.push({
        text: "Name",
        align: "left",
        value: "name",
        sortable: true,
      });

      this.headers.push({
        text: "Create At",
        align: "left",
        value: "date",
        sortable: true,
      });
      // this.headers.push({
      //   text: "Invoice Number",
      //   align: "left",
      //   value: "localId",
      //   sortable: true,
      // });

      // if (!this.DonorId && !this.CustomerId) {
      //   this.headers.push({
      //     text: "Customer Name",
      //     value: "Customer.name",
      //     align: "left",
      //     sortable: true,
      //   });
      // }

      this.headers.push({
        text: "Due Date",
        align: "left",
        value: "dueDate",
        sortable: true,
      });

      // this.headers.push({
      //   text: "Transactions",
      //   align: "center",
      //   value: "transactionCount",
      //   sortable: true
      // });

      // this.headers.push({
      //   text: "Total",
      //   value: "total",
      //   align: "right",
      //   sortable: true,
      // });

      // this.headers.push({
      //   text: "Payment",
      //   value: "payment",
      //   align: "right",
      //   sortable: true,
      // });

      this.headers.push({
        text: "Amount",
        value: "due",
        align: "right",
        sortable: true,
      });
      this.headers.push({
        text: "",
        value: "action",
        sortable: false,
      });
    },
    getList() {
      this.loading = true;
      let where = {};
      if (!this.showAll) {
        where = {
          start: this.dateTime.start,
          end: this.dateTime.end,
        };
      }
      if (this.DonorId) {
        where.DonorId = this.DonorId;
      }

      if (this.CustomerId) {
        where.CustomerId = this.CustomerId;
      }

      where.showCustomersOnly = this.showCustomersOnly;
      where.showDonorsOnly = this.showDonorsOnly;

      if (this.due) {
        where.due = true;
      }
      console.log("running", where);
      return invoiceService
        .getAll(where)
        .then((response) => {
          console.log("invoice", response);
          this.loading = false;
          this.Invoices = response.data || [];
          this.fullTotal = 0;
          this.fullPayment = 0;
          this.fullBalance = 0;
          this.Invoices.map((row) => {
            this.fullTotal += row.total;
            this.fullPayment += row.payment;
            this.fullBalance += row.due;
            if (row.Customer) row.name = row.Customer.name;
            else if (row.Donor) row.name = row.Donor.name;
          });
          return response;
        })
        .catch((error) => {
          console.log("getList", error);
          this.loading = false;
        });
    },
    editInvoice(invoice) {
      // console.log('ddf', invoice)
      this.$router.push({ path: `/invoice/${invoice.id}/edit` });
    },
    deleteInvoice(invoice) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.loadingDialog = true;
          invoiceService
            .delete(invoice.id)
            .then((response) => {
              console.log("response delete", response);
              this.loadingDialog = false;
              if (response.data) {
                this.$events.emit("paymentListEvent");
                this.$events.emit("customerProfileEvent");
                this.$swal
                  .mixin({
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                  })
                  .fire({
                    icon: "success",
                    title: "Invoice has been deleted.",
                  });
                // this.$swal("Deleted!", "Invoice has been deleted.", "success");
                this.getList();
              }
            })
            .catch((error) => {
              this.loadingDialog = false;
              this.$swal({
                title: "Delete Invoice",
                text: error.data.message,
                icon: "error",
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
/* Shared page, heading, metrics, card, toolbar, search, table,
   row hover, and empty-state styles come from app.scss. */

.invoice-list-embedded {
  padding: 14px;
  background: white;
}

.invoice-metrics {
  margin: 0 0 10px;
}

.invoice-metric-action {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.invoice-metric-action .v-btn {
  height: 42px;
  border-radius: 9px;
  text-transform: none;
  letter-spacing: 0;
}

.invoice-toolbar-actions {
  flex: 1;
  justify-content: flex-end;
}

.audience-filter {
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid #dce4dc;
  border-radius: 8px;
  box-shadow: none;
}

.audience-filter .v-btn {
  height: 36px;
  color: #657268;
  border: 0 !important;
  background: white;
  text-transform: none;
  letter-spacing: 0;
}

.audience-filter .v-btn--active {
  color: #2e7d32 !important;
  background: #eaf6eb !important;
}

.date-filter {
  width: 300px;
}

.invoice-number {
  color: #2e7d32;
  font-weight: 750;
}

.invoice-account-name {
  margin-left: 5px;
  color: #2e3b31;
  font-size: 12px;
  font-weight: 700;
}

.collection-attempt-chip {
  cursor: pointer;
}

@media (max-width: 1100px) {
  .invoice-table {
    overflow-x: auto;
  }

  .invoice-table ::v-deep table {
    min-width: 900px;
  }
}

@media (max-width: 959px) {
  .invoice-toolbar-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .date-filter {
    width: min(100%, 320px);
  }
}

@media (max-width: 599px) {
  .invoice-list-embedded {
    padding: 0;
  }

  .invoice-metrics {
    margin-bottom: 8px;
  }

  .invoice-metric-action {
    justify-content: stretch;
  }

  .invoice-metric-action .v-btn {
    width: 100%;
  }

  .invoice-toolbar-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .audience-filter {
    display: flex;
    width: 100%;
  }

  .audience-filter .v-btn {
    flex: 1;
    padding: 0 7px !important;
  }

  .date-filter {
    width: 100%;
  }
}
</style>
